import { MAX_ENVELOPE_NETWORKS, type WifiNetwork } from '../../api/wifi-types.js';
import { h, type View } from './dom.js';

export interface NetworkPicker extends View {
  /** Chosen network ids, in the list's priority order. */
  selected(): string[];
  setNetworks(networks: readonly WifiNetwork[]): void;
  setDisabled(value: boolean): void;
  /** How many networks the target unit keeps; null while no unit has been found yet. */
  setLimit(limit: number | null): void;
}

let counter = 0;

/** Checklist of account networks. The list order is the priority order the unit will try them in. */
export function createNetworkPicker(options: { legend: string; onChange?(ids: string[]): void }): NetworkPicker {
  const id = `network-picker-${++counter}`;
  let networks: WifiNetwork[] = [];
  const chosen = new Set<string>();
  let primed = false;
  let disabled = false;
  let limit: number | null = null;
  const count = h('p', { class: 'card-sub', id: `${id}-count`, 'aria-live': 'polite' });
  const list = h('ul', { class: 'network-picker-list' });
  const root = h('fieldset', { class: 'network-picker', 'aria-describedby': `${id}-count` }, [h('legend', {}, [options.legend]), list, count]);

  function ids(): string[] { return networks.filter(network => chosen.has(network.id)).map(network => network.id); }
  /** The order only matters when several networks will really be sent. */
  function orderSentence(chosen: number): string {
    if (chosen < 2 || (limit !== null && limit < 2)) return '';
    return limit === null ? ' Si el aparato admite varias, las probará en este orden.' : ' El aparato las probará en este orden.';
  }
  function paint(): void {
    const order = ids();
    count.textContent = networks.length === 0 ? ''
      : order.length === 0 ? `Ninguna elegida. Puedes elegir hasta ${MAX_ENVELOPE_NETWORKS}.`
        : `${order.length} de ${MAX_ENVELOPE_NETWORKS} elegidas.${orderSentence(order.length)}`;
    for (const item of list.querySelectorAll<HTMLLIElement>('li')) {
      const box = item.querySelector<HTMLInputElement>('input')!;
      const position = item.querySelector<HTMLElement>('.network-picker-order')!;
      const index = order.indexOf(item.dataset.network ?? '');
      position.textContent = index >= 0 ? `${index + 1}.` : '';
      box.checked = index >= 0;
      box.disabled = disabled || (index < 0 && order.length >= MAX_ENVELOPE_NETWORKS);
    }
  }
  function render(): void {
    list.replaceChildren(...networks.map(network => {
      const box = h('input', { type: 'checkbox', 'aria-label': `Enviar la red ${network.ssid}` });
      box.addEventListener('change', () => {
        if (box.checked && chosen.size < MAX_ENVELOPE_NETWORKS) chosen.add(network.id); else chosen.delete(network.id);
        paint();
        options.onChange?.(ids());
      });
      const name = h('span', { class: 'network-picker-name' });
      name.textContent = network.ssid;
      const row = h('li', { class: 'network-picker-item', dataset: { network: network.id } }, [h('label', { class: 'field inline' }, [box, h('span', { class: 'network-picker-order', 'aria-hidden': 'true' }), name])]);
      return row;
    }));
    paint();
  }

  return {
    el: root,
    selected: ids,
    setNetworks(next) {
      networks = [...next];
      const known = new Set(networks.map(network => network.id));
      for (const kept of [...chosen]) if (!known.has(kept)) chosen.delete(kept);
      // The first list that arrives starts with the networks marked «Compartir con mis dispositivos».
      if (!primed && networks.length) {
        primed = true;
        for (const network of networks) if (network.shared && chosen.size < MAX_ENVELOPE_NETWORKS) chosen.add(network.id);
      }
      render();
      options.onChange?.(ids());
    },
    setDisabled(value) { disabled = value; root.disabled = value; paint(); },
    setLimit(value) { if (value === limit) return; limit = value; paint(); },
    destroy() { chosen.clear(); networks = []; list.replaceChildren(); },
  };
}
